import React, { RefObject, useEffect, useRef } from 'react';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import classnames from 'classnames/bind';

import COLOR from '@CONSTANTS/COLOR.constant';

import { notify } from '@NOTIFICATION/Notificator';

import useAuth from '@HOOKS/useAuth';
import usePublishSectionBaseTextModal from '@HOOKS/store/modals/usePublishSectionBaseTextModal';
import { usePublishSectionBaseText } from '@SERVICES';

import BaseModal from '@MODALS/BaseModal';
import HiddenFormButton from '@COMPONENTS/COMMON/buttons/HiddenFormButton';
import PublicationForm, { PublishFormValues } from '@COMPONENTS/SHARED/PublicationForm';
import BasicButton from '@COMPONENTS/COMMON/buttons/BasicButton';

import styles from './PublishSectionBaseTextModal.module.scss';

const cx: CX = classnames.bind(styles);

function PublishSectionBaseTextModal() {
    const { state: { user } } = useAuth();

    const {
        state: { data: modalParams },
        actions: publishSectionBaseTextModalActions,
    } = usePublishSectionBaseTextModal(() => true);

    const [
        publishSectionBaseText,
        {
            isLoading, isSuccess, isError, error,
        },
    ] = usePublishSectionBaseText();

    const {
        id: sectionId, body, documentId, chapterId,
    } = modalParams!;

    const formButtonRef: RefObject<HTMLButtonElement> = useRef(null);

    useEffect(() => {
        if (isSuccess) {
            notify.info('Section introduction text has been published', {
                toastId: 'publish-section-base-text',
            });
            publishSectionBaseTextModalActions.confirm();
            publishSectionBaseTextModalActions.close();
        }
    }, [isSuccess, publishSectionBaseTextModalActions]);

    useEffect(() => {
        if (isError) {
            if ((error as FetchBaseQueryError).status === 403) {
                notify.error('Permission denied', {
                    toastId: 'publish-section-base-text-permission-error',
                });
            } else {
                notify.error('Something wrong!', {
                    toastId: 'publish-section-base-text-error',
                });
            }
        }
    }, [isError, error]);

    function onSave(formValues: PublishFormValues | null, includeToVersionControl: boolean) {
        let description = null;

        if (includeToVersionControl && formValues) {
            description = formValues?.description;
        }

        publishSectionBaseText({
            id: sectionId,
            body,
            description,
            userId: user!.id,
            documentId,
            chapterId,
        });
    }

    function handleClose() {
        publishSectionBaseTextModalActions.close();
    }

    return (
        <BaseModal
            onClose={() => handleClose()}
        >
            <div className={cx('publish-section-base-text-modal')}>
                <div className={cx('title')}>
                    {body && 'Publish section introduction text'}
                    {!body && 'Publish section without introduction'}
                </div>

                {body && (
                    <PublicationForm
                        onSave={(formValues: PublishFormValues, includeToVersionControl) => {
                            onSave(formValues, includeToVersionControl);
                        }}
                    >
                        <HiddenFormButton ref={formButtonRef} />
                    </PublicationForm>
                )}

                <div className={cx('footer')}>
                    <BasicButton
                        title="Publish"
                        isProcessing={isLoading}
                        style={{
                            height: 40,
                            width: '100%',
                            fontSize: 14,
                            backgroundColor: COLOR['app-green'],
                            color: 'white',
                        }}
                        onClick={() => {
                            formButtonRef.current?.click();
                            if (!formButtonRef.current && !body) {
                                onSave(null, false);
                            }
                        }}
                    />
                </div>
            </div>
        </BaseModal>
    );
}

export default PublishSectionBaseTextModal;
